import React, { useState, useCallback, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  useNavigate
} from "react-router-dom";
import "./App.css";
import LocationStationSearch from "./components/LocationStationSearch";
import CapacityChart from "./components/CapacityChart";
import { Station, VehicleTypeData, VehicleTypes } from "./types";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Component for the home page with station search
function HomePage() {
  const navigate = useNavigate();

  const handleStationSelect = useCallback(
    (station: Station) => {
      navigate(`/station/${station.station_id}`);
    },
    [navigate]
  );

  return (
    <div className="App">
      <header className="App-header">
        <h1>🚴‍♂️ CitiBike Station Capacity Monitor</h1>
        <p>Track real-time capacity data for CitiBike stations</p>
      </header>

      <main className="App-main">
        <div className="search-section">
          <LocationStationSearch onStationSelect={handleStationSelect} />
        </div>
      </main>
    </div>
  );
}

// Component for individual station view
function StationPage() {
  const { stationId } = useParams<{ stationId: string }>();
  const navigate = useNavigate();
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [vehicleTypeData, setVehicleTypeData] = useState<VehicleTypeData[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypes>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVehicleTypes = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/vehicle-types`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const types = await response.json();
      setVehicleTypes(types);
    } catch (err) {
      console.error("Failed to fetch vehicle types:", err);
    }
  }, []);

  const fetchStationData = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/stations`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const stations = await response.json();
      const station = stations.find((s: Station) => s.station_id === id);

      if (station) {
        setSelectedStation(station);
        setError(null);
      } else {
        setError("Station not found");
        setSelectedStation(null);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch station data"
      );
      setSelectedStation(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVehicleTypeData = useCallback(
    async (
      stationId: string,
      startTime: string,
      endTime: string,
      vehicleTypeId?: string
    ) => {
      setLoading(true);
      setError(null);

      try {
        let url = `${API_BASE_URL}/api/capacity/${stationId}?start_time=${startTime}&end_time=${endTime}`;
        if (vehicleTypeId) {
          url += `&vehicle_type_id=${vehicleTypeId}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setVehicleTypeData(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to fetch vehicle type data"
        );
        setVehicleTypeData([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleTimeRangeChange = useCallback(
    (startTime: string, endTime: string, vehicleTypeId?: string) => {
      if (selectedStation) {
        fetchVehicleTypeData(
          selectedStation.station_id,
          startTime,
          endTime,
          vehicleTypeId
        );
      }
    },
    [selectedStation, fetchVehicleTypeData]
  );

  useEffect(() => {
    fetchVehicleTypes();
  }, [fetchVehicleTypes]);

  useEffect(() => {
    if (stationId) {
      fetchStationData(stationId);
    }
  }, [stationId, fetchStationData]);

  if (loading && !selectedStation) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>🚴‍♂️ CitiBike Station Capacity Monitor</h1>
        </header>
        <main className="App-main">
          <div className="loading">Loading station...</div>
        </main>
      </div>
    );
  }

  if (error && !selectedStation) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>🚴‍♂️ CitiBike Station Capacity Monitor</h1>
        </header>
        <main className="App-main">
          <div className="error">Error: {error}</div>
          <button onClick={() => navigate("/")} className="back-button">
            Back to Search
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>🚴‍♂️ CitiBike Station Capacity Monitor</h1>
        <p>Track real-time capacity data for CitiBike stations</p>
        <button onClick={() => navigate("/")} className="back-button">
          ← Back to Search
        </button>
      </header>

      <main className="App-main">
        {selectedStation && (
          <div className="chart-section">
            <div className="station-info">
              <h2>{selectedStation.name}</h2>
              <p>Station ID: {selectedStation.station_id}</p>
              <p>Current Capacity: {selectedStation.capacity}</p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert("Station URL copied to clipboard!");
                }}
                className="share-button"
              >
                📋 Share Station URL
              </button>
            </div>

            <CapacityChart
              data={vehicleTypeData}
              vehicleTypes={vehicleTypes}
              loading={loading}
              error={error}
              onTimeRangeChange={handleTimeRangeChange}
            />
          </div>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/station/:stationId" element={<StationPage />} />
      </Routes>
    </Router>
  );
}

export default App;
